/**
 * #2193 — a lead answers a member's fixed-shift ask.
 *
 *   * `useDecideFixedShiftAsk` — the Inbox card's Approve / Decline:
 *     `PATCH /api/cycles/{window}/fixed-shifts/{id}` with
 *     `{status: "approved" | "rejected", note}`. The route closes every
 *     lead's card on the ask, records the note on the row
 *     (`decision_note`), and sends the member her answer.
 *   * `useFixedShiftAsks` — `GET /api/teams/{id}/fixed-shift-asks`, the
 *     team's asks still waiting, for the laptop's right panel ("2 fixed-shift
 *     asks to answer" → the Inbox). A lead / co-lead read; anyone else gets
 *     nothing (`enabled` is the caller's to set).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";
import { memberInboxKey } from "./useMemberInbox";

export type FixedShiftAsksResponse = components["schemas"]["FixedShiftAsksResponse"];
export type FixedShiftAskRow = components["schemas"]["FixedShiftAskRow"];

export const FIXED_SHIFT_ASKS_KEY = ["fixed-shift-asks"] as const;

export function useFixedShiftAsks(teamId: string | undefined, { enabled = true } = {}) {
  const { accountId } = useAuth();
  return useQuery<FixedShiftAsksResponse, ApiError | Error>({
    queryKey: [...FIXED_SHIFT_ASKS_KEY, teamId ?? "", accountId ?? ""],
    enabled: Boolean(teamId && accountId) && enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: () => apiFetch<FixedShiftAsksResponse>(`/api/teams/${teamId}/fixed-shift-asks`),
  });
}

export interface DecideFixedShiftAskInput {
  cycleId: string;
  fixedShiftId: string;
  approve: boolean;
  note?: string | null;
}

export function useDecideFixedShiftAsk() {
  const queryClient = useQueryClient();
  const { accountId } = useAuth();
  return useMutation<unknown, ApiError | Error, DecideFixedShiftAskInput>({
    mutationFn: ({ cycleId, fixedShiftId, approve, note }) =>
      apiFetch(`/api/cycles/${cycleId}/fixed-shifts/${fixedShiftId}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: approve ? "approved" : "rejected",
          note: note?.trim() ? note.trim() : null,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberInboxKey(accountId) });
      queryClient.invalidateQueries({ queryKey: FIXED_SHIFT_ASKS_KEY });
      queryClient.invalidateQueries({ queryKey: ["cycle-fixed-shifts"] });
      queryClient.invalidateQueries({ queryKey: ["me-fixed-shifts"] });
    },
  });
}
